import {useRef} from 'react';
import Anchor from './Anchor';
import BigOverContentCardBgV4 from './BigOverContentCardBgV4';
import BigOverContentCardInfoV4 from './BigOverContentCardInfoV4';
import ContentCardGradientMaskV4 from './ContentCardGradientMaskV4';
import ContentThumbnailV4 from './ContentThumbnailV4';
import RankingCriteria from './RankingCriteria';
import useContentVideoV4 from '../../commonSub/hook/useContentVideoV4';
import useIsLite from '../../commonSub/hook/useIsLite';
import {IBigContent} from '../../commonSub/type';
import {IRankingInfo} from '../../home/common/type';
import {IVideo} from '../type';
import {routeTo} from '../util/PageRouter';

interface IProps {
  content: IBigContent;
  showAuthors?: boolean;
  titleText?: string;
  blackBadgeList?: string[];
  eventIpVideo?: IVideo;
  rankingInfo?: IRankingInfo;
  onClickContent?: () => void;
  onSendViewImp?: () => void;
  onClickPromotionVideo?: () => void;
  onClickPromotionVideoMute?: (isMute: boolean) => void;
  onClickPromotionVideoPlay?: () => void;
}

export default function BigOverContentCardV4({
  content,
  titleText,
  showAuthors = true,
  blackBadgeList,
  eventIpVideo,
  rankingInfo,
  onClickContent,
  onSendViewImp,
  onClickPromotionVideo,
  onClickPromotionVideoMute,
  onClickPromotionVideoPlay,
}: IProps) {
  const isLite = useIsLite();
  const {
    badges,
    bgColor,
    bgColorRgb,
    title,
    titleImg,
    mainImg,
    motionVideo,
    motionStartImg,
    promotionVideo,
    bgImg,
    landingUrl,
    authors,
    descLine2,
    lastModified,
  } = content;
  const description = showAuthors ? authors : descLine2;

  const wrapperRef = useRef<HTMLAnchorElement>(null);
  const imgRef = useRef<HTMLPictureElement>(null);

  const {MotionVideoComponent, IpVideoComponent, showIpVideo} =
    useContentVideoV4({
      wrapperRef,
      motionImgRef: imgRef,
      motionVideo,
      motionVideoPoster: motionStartImg,
      motionVideoAspectRatio: 0,
      title,
      onSendViewImp,
      promotionVideo,
      eventIpVideo,
    });

  const transitionCn = cn('transition-opacity duration-[600ms]', {
    'opacity-0': showIpVideo,
    'opacity-100': !showIpVideo,
  });

  return (
    <div className="relative">
      <Anchor
        ref={wrapperRef}
        className="relative overflow-hidden"
        pageInfo={landingUrl}
        onClick={() => {
          onClickContent?.();
          landingUrl && routeTo(landingUrl);
        }}
      >
        <BigOverContentCardBgV4
          bgImg={bgImg}
          hasLastModified={!!lastModified}
        />
        <div
          className={cn(
            'h-full absolute -inset-x-11 top-0 pb-50',
            transitionCn
          )}
        >
          <div className="w-full h-full relative">
            {(isLite || !motionVideo) && (
              <ContentThumbnailV4
                ref={imgRef}
                src={mainImg}
                className={THUMBNAIL_CN}
              />
            )}
            <MotionVideoComponent
              className={THUMBNAIL_CN}
              posterClassName={THUMBNAIL_CN}
            />
          </div>
        </div>
        <ContentCardGradientMaskV4
          className={cn(
            'inset-x-0 bottom-0 z-2 rounded-16',
            '!h-[150px] sm:!h-[170px] lg:!h-[200px]'
          )}
          style={{
            backgroundImage: `linear-gradient(
              180deg,
              rgba(${bgColorRgb}, 0) 0%,
              rgba(${bgColorRgb}, 0.7) 33.33%,
              ${bgColor} 66.67%,
              ${bgColor} 100%
            )`,
          }}
        />
        <BigOverContentCardInfoV4
          className={transitionCn}
          title={title}
          titleImg={titleImg}
          titleText={titleText}
          description={description}
          rankingInfo={rankingInfo}
          badgeListClassName="!w-auto flex items-center gap-x-2"
          badges={badges}
          blackBadgeList={blackBadgeList}
        />
      </Anchor>
      <IpVideoComponent
        className={cn(
          'overflow-hidden rounded-16',
          lastModified ? 'top-32' : 'top-24'
        )}
        landingUrl={landingUrl}
        onClickIpVideo={onClickPromotionVideo}
        onClickIpVideoMute={onClickPromotionVideoMute}
        onClickIpVideoPlay={onClickPromotionVideoPlay}
      />
      {lastModified && <RankingCriteria lastModifiedDate={lastModified} />}
    </div>
  );
}

const THUMBNAIL_CN = '!object-contain object-top w-full h-full';
